import { useCallback } from 'react';

import { setSupportChatSource } from './useSupportChatAnalytics';

/** Return value of the {@link useSupportChat} hook. */
interface IUseSupportChatReturn {
  /** Opens the Zendesk support chat widget. */
  openSupportChat: () => void;
}

/**
 * Provides a function to programmatically open the Zendesk support chat widget.
 *
 * Locates the Zendesk launcher iframe and clicks the chat button using known
 * selectors.
 *
 * @returns An object containing the openSupportChat handler.
 *
 * @example
 * const { openSupportChat } = useSupportChat();
 * <Button onClick={openSupportChat}>Contact Support</Button>
 */
function useSupportChat(): IUseSupportChatReturn {
  const openSupportChat = useCallback((): void => {
    const iframe = document.getElementById('launcher') as HTMLIFrameElement | null;
    if (iframe?.contentWindow && iframe?.contentDocument) {
      const selectors = [
        'button[data-garden-id="buttons.icon_button"]',
        'button[aria-label="Open messaging window"]',
      ];

      for (const selector of selectors) {
        const chatButton = iframe.contentDocument?.querySelector(selector);
        if (chatButton) {
          setSupportChatSource('in_app');
          (chatButton as HTMLElement).click();
          return;
        }
      }
    }
  }, []);

  return { openSupportChat };
}

export default useSupportChat;
